import {
  ResourceStatus,
  ViewableApplicationResource,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { BackupHealthStatus, STATUS_TEXT } from 'src/app/account/components/application/statusText'
import { AppStatusState } from 'src/app/account/components/process-state/process-state.component'
import { Application } from 'src/app/account/services/application.service'
import { SyncPhaseDto } from 'src/app/services/dtos'
import { getResourceTypeDisplayString } from 'src/app/shared/utils'
import { ResourceType, getResourceTypeDetails } from 'src/utils/resource-types'
import { Arn } from 'src/utils/types'
import {
  MockApplicationOptions,
  RecoveryScreenTabBarFlags,
  buildMockApplication,
  tabBarFailover,
  tabBarStandby,
} from './showcase-mock-core'

export interface BackupScreenScenario {
  key: string
  label: string
  description: string
  // optgroup label in the dropdown.
  group: 'STANDBY' | 'FAILOVER'
  syncPhase: SyncPhaseDto
  application: Application
  resources: ViewableApplicationResource[]
  status: BackupHealthStatus
  tabBarFlags: RecoveryScreenTabBarFlags
}

interface MockBackupResourceOpts {
  name: string
  type: ResourceType
  status: ResourceStatus
  progress?: number
  message?: string
  substatus?: string
  arn?: string
}

// ViewableApplicationResource POJO factory for Backup-context rows. Substatus
// rules mirror the production computeResourceSubstatus output for backup
// scope: INIT → "Waiting in queue..." (or "Confirming status..." during the
// post-job-lag bridge), PROCESSING → progress %, FAILED → message verbatim.
// Pilot-light / orphaned-target overrides don't apply to backup scope.
function mkBackupResource(opts: MockBackupResourceOpts): ViewableApplicationResource {
  const { name, type, status, progress = 0, message = null, substatus, arn } = opts

  const computedSubstatus = (() => {
    if (substatus !== undefined) return substatus
    switch (status) {
      case ResourceStatus.PROCESSING:
        return progress > 0 && progress < 1 ? `${Math.round(progress * 100)}%` : message || undefined
      case ResourceStatus.PENDING:
        return message || 'Waiting in queue...'
      case ResourceStatus.FAILED:
        return message || undefined
      case ResourceStatus.NOT_PROCESSED:
        return 'Upstream dependency failed'
      default:
        return undefined
    }
  })()

  return {
    resourceType: type,
    resourceVariant: null,
    resourceArn: (arn ?? `arn:aws:MOCK-TEST:us-east-1:123456789012:${type}/${name}`) as Arn,
    sourceResourceName: name,
    // Backup-context table only shows the source column meaningfully — target
    // is implicit (the backup destination). Production sometimes renders
    // target details too, but the mock can leave them empty without crashing.
    targetResourceName: '',
    resourceProperties: [],
    status,
    progress,
    message,
    substatus: computedSubstatus,
    issues: new Set(),
    displayResourceType: getResourceTypeDisplayString(type, null),
    resourceTypeDetails: getResourceTypeDetails({ resourceType: type }),
  } as ViewableApplicationResource
}

// Resource-set builders per scenario.
function resourcesBackupPlanning(): ViewableApplicationResource[] {
  // Queued resources whose timestamps haven't caught up. NO_STATUS dominates.
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.PENDING }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.NO_STATUS }),
  ]
}

function resourcesBackupInitial(): ViewableApplicationResource[] {
  // First-attempt-in-progress: many PROCESSING + a few PENDING in the queue.
  return [
    mkBackupResource({
      name: 'web-app-01',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.PROCESSING,
      progress: 0.3,
    }),
    mkBackupResource({
      name: 'web-app-02',
      type: ResourceType.ec2Instance,
      status: ResourceStatus.PROCESSING,
      progress: 0.6,
    }),
    mkBackupResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.PROCESSING,
      progress: 0.15,
    }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.PENDING }),
    mkBackupResource({ name: 'reporting-fn', type: ResourceType.lambdaFunction, status: ResourceStatus.PENDING }),
    mkBackupResource({ name: 'orders', type: ResourceType.dynamoDbTable, status: ResourceStatus.PENDING }),
  ]
}

function resourcesBackupSubsequent(): ViewableApplicationResource[] {
  // Subsequent attempt: mix of PROCESSING (the resources being re-backed-up)
  // and SUCCEEDED (those already complete this cycle).
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.PROCESSING,
      progress: 0.7,
    }),
    mkBackupResource({
      name: 'app-data',
      type: ResourceType.s3Bucket,
      status: ResourceStatus.PROCESSING,
      progress: 0.4,
    }),
    mkBackupResource({ name: 'reporting-fn', type: ResourceType.lambdaFunction, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'orders', type: ResourceType.dynamoDbTable, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBackupSucceeded(): ViewableApplicationResource[] {
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'reporting-fn', type: ResourceType.lambdaFunction, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'orders', type: ResourceType.dynamoDbTable, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBackupScanResults(): ViewableApplicationResource[] {
  // Successful backup with malware-scan pills mixed in. SCAN_INFECTED on a
  // bucket, SCAN_FAILED on another, SCAN_CLEAN on a third, plain SUCCEEDED
  // for resources that don't get scanned.
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SCAN_CLEAN }),
    mkBackupResource({ name: 'user-uploads', type: ResourceType.s3Bucket, status: ResourceStatus.SCAN_INFECTED }),
    mkBackupResource({ name: 'archive-logs', type: ResourceType.s3Bucket, status: ResourceStatus.SCAN_FAILED }),
    mkBackupResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBackupFailed(): ViewableApplicationResource[] {
  // Mixed SUCCEEDED + FAILED, with a NOT_PROCESSED row blocked downstream of
  // the failure to show the upstream-dependency pill in backup context too.
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({
      name: 'db-primary',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.FAILED,
      message: 'Snapshot quota exceeded in source region.',
    }),
    mkBackupResource({
      name: 'db-replica',
      type: ResourceType.rdsDbInstance,
      status: ResourceStatus.NOT_PROCESSED,
    }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.SUCCEEDED }),
    mkBackupResource({ name: 'orders', type: ResourceType.dynamoDbTable, status: ResourceStatus.SUCCEEDED }),
  ]
}

function resourcesBackupJobLevelFailure(): ViewableApplicationResource[] {
  // Job itself failed before reaching any resource. Most rows show NO_STATUS
  // since the run never produced per-resource updates.
  return [
    mkBackupResource({ name: 'web-app-01', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'web-app-02', type: ResourceType.ec2Instance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'db-primary', type: ResourceType.rdsDbInstance, status: ResourceStatus.NO_STATUS }),
    mkBackupResource({ name: 'app-data', type: ResourceType.s3Bucket, status: ResourceStatus.NO_STATUS }),
  ]
}

// Time-string + RP suffix helpers tuned for the Backup status text strings.
const RP_AT_1_14_MAY_8 = ' from recovery point at 01:14 PM on May 8'

const RP_AT_11_14_MAY_8 = ' from recovery point at 11:14 AM on May 8'

const FAILED_AT_10_25_MAY_9 = ' at 10:25 AM on May 9'

export function buildBackupScreenScenarios(): BackupScreenScenario[] {
  const standbyApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.STANDBY, ...overrides })
  const failoverApp = (overrides?: Partial<MockApplicationOptions>) =>
    buildMockApplication({ syncPhase: SyncPhaseDto.FAILOVER, ...overrides })

  return [
    // ─── Standby ────────────────────────────────────────────────────
    {
      key: 'b1-planning-backup',
      label: 'Planning backup (queued)',
      description:
        'NEVER_ATTEMPTED state with queued resources whose timestamps have not caught up. Bridges to first-attempt-in-progress.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupPlanning(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.backup.planningBackup,
        issues: [],
      },
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'b2-initial-backup-in-progress',
      label: 'Creating initial backup',
      description:
        'First-attempt-in-progress. Inline progress bar (N/M resources). No prior successful backup yet so TEST + LAUNCH RECOVERY remain disabled.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupInitial(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.backup.creatingInitial,
        // resourcesBackupInitial has 0 SUCCEEDED of 6 (3 PROCESSING + 3 PENDING).
        progressCount: 0,
        progressTotal: 6,
        issues: [],
      },
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },
    {
      key: 'b3-subsequent-backup-in-progress',
      label: 'Creating subsequent backup',
      description:
        'A backup is running with a prior successful backup on record. Status bar shows progress bar AND the prior-backup summary at far right.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupSubsequent(),
      status: {
        state: AppStatusState.INDETERMINATE,
        text: STATUS_TEXT.backup.creatingNew,
        progressCount: 4,
        progressTotal: 6,
        priorBackupInfo: STATUS_TEXT.backup.priorBackupInfo('2 hours ago', RP_AT_1_14_MAY_8, 6),
        issues: [],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b4-last-successful-clean',
      label: 'Last successful — clean',
      description: 'Steady-state successful backup. All rows SUCCEEDED, no issues.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupSucceeded(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', `${RP_AT_1_14_MAY_8}.`),
        issues: [],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b5-last-successful-with-issues',
      label: 'Last successful — with open issues',
      description:
        'Successful backup but issues are open. Status state is WARNING; the "Review and fix N issues" link appears next to the headline.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupSucceeded(),
      status: {
        state: AppStatusState.WARNING,
        text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', `${RP_AT_1_14_MAY_8}.`),
        issues: [{} as any, {} as any],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b6-scan-results-mixed',
      label: 'Last successful — with malware-scan results',
      description:
        'Successful backup with mixed scan-result pills (clean / infected / scan failed). WARNING because at least one bucket is infected.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupScanResults(),
      status: {
        state: AppStatusState.WARNING,
        text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', `${RP_AT_1_14_MAY_8}.`),
        issues: [{} as any],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b7-failed-n-resources',
      label: 'Failed N resources',
      description:
        'Per-resource failures: status bar shows clickable failed-link with the count and the failure timestamp. Prior backup info renders in the substatus slot.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupFailed(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.backup.failedToBackupCount(1, FAILED_AT_10_25_MAY_9),
        failedLink: { ...STATUS_TEXT.backup.failedLinkBackup, count: 1, suffix: `${FAILED_AT_10_25_MAY_9}.` },
        priorBackupInfo: STATUS_TEXT.backup.lastSuccessful('1 day ago', `${RP_AT_11_14_MAY_8}.`),
        issues: [],
      },
      tabBarFlags: tabBarStandby(),
    },
    {
      key: 'b8-job-level-failure',
      label: 'Failed to create backup (job-level)',
      description:
        'Job itself failed without any individual resource failures. No clickable failed-link. Prior-backup info renders in the substatus slot.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: resourcesBackupJobLevelFailure(),
      status: {
        state: AppStatusState.ERROR,
        text: STATUS_TEXT.backup.failedToCapture(FAILED_AT_10_25_MAY_9),
        priorBackupInfo: STATUS_TEXT.backup.lastSuccessful('1 day ago', `${RP_AT_11_14_MAY_8}.`),
        issues: [],
      },
      tabBarFlags: tabBarStandby(),
    },

    // ─── Failover (frozen last-known) ───────────────────────────────
    {
      key: 'b9-frozen-during-failover',
      label: 'Frozen last-known result during failover',
      description:
        'App is in FAILOVER — Backup tab shows the last-known result from before the failover (no active backup polling).',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesBackupSucceeded(),
      status: {
        state: AppStatusState.SUCCESS,
        text: STATUS_TEXT.backup.lastSuccessful('3 hours ago', ` from recovery point at 12:14 PM on May 8.`),
        issues: [],
      },
      tabBarFlags: tabBarFailover(),
    },
    {
      key: 'b10-no-resources-configured',
      label: 'No resources configured (empty)',
      description:
        'Brand-new app with no resources configured. With an empty backup resource list, production short-circuits to this INFO placeholder before any status computation; resource table is empty.',
      group: 'STANDBY',
      syncPhase: SyncPhaseDto.STANDBY,
      application: standbyApp(),
      resources: [],
      status: {
        // Verbatim from application.component.ts: the empty-resource guard emits this
        // (not STATUS_TEXT.backup.noDataAvailable) when backupResources.length === 0.
        state: AppStatusState.INFO,
        text: 'No data available yet. Configure your resources to create initial backup.',
        issues: [],
      },
      tabBarFlags: tabBarStandby({ stagingSuccess: false }),
    },

    // ─── Failover (frozen last-known) ───────────────────────────────
    {
      key: 'b11-no-backup-data',
      label: 'No backup data available',
      description:
        'Resources exist (all NO_STATUS) but no backup has ever produced a recovery point, during a non-standby phase. computeBackupHealthLastKnown surfaces the noDataAvailable placeholder as INFO (no spinner glyph).',
      group: 'FAILOVER',
      syncPhase: SyncPhaseDto.FAILOVER,
      application: failoverApp(),
      resources: resourcesBackupJobLevelFailure(),
      status: {
        state: AppStatusState.INFO,
        text: STATUS_TEXT.backup.noDataAvailable,
        issues: [],
      },
      tabBarFlags: tabBarFailover(),
    },
  ]
}

// ─────────────────────────────────────────────────────────────────────────────
// Usage & Billing dashboard scenarios (ARP-7657)
//
// UsageBillingComponent fetches everything through one call, so the test page
// patches UsageBillingService.loadDashboard to return one of these snapshots
// (see CLAUDE-TEST-PAGE.md). Each scenario is a fully-realized UsageDashboardVm
// — no live polling, no partial fetches.
// ─────────────────────────────────────────────────────────────────────────────
